import { useState, useEffect, useRef } from 'react';
import { cleanModelDisplay } from '@/components/ModelSelector';
import { Typography, Button, Dropdown, Space, message } from 'antd';
import {
  RobotOutlined, UserOutlined, CopyOutlined, DownOutlined, RightOutlined,
  ThunderboltOutlined, GiftOutlined, DownloadOutlined, LinkOutlined, FileTextOutlined,
} from '@ant-design/icons';
import { ChatMessage as ChatMessageType } from '@/stores/chat';
import { aiAPI } from '@/services/api';

const { Text } = Typography;

interface Props {
  msg: ChatMessageType;
  /** 流式打字完成回调（用于清除 streaming 标记） */
  onStreamDone?: (id: string) => void;
}

/** Codex 风格流式打字：按内容长度自适应步长，展示完成时回调 */
function useTypewriter(text: string, enabled: boolean, speed = 14) {
  const [len, setLen] = useState(enabled ? 0 : text.length);
  const doneRef = useRef(!enabled || !text);
  useEffect(() => {
    if (!enabled || !text) {
      setLen(text ? text.length : 0);
      doneRef.current = !enabled || !text;
      return;
    }
    setLen(0);
    doneRef.current = false;
    let i = 0;
    const step = Math.max(1, Math.round(text.length / 90));
    const timer = setInterval(() => {
      i += step;
      if (i >= text.length) {
        clearInterval(timer);
        i = text.length;
        doneRef.current = true;
      }
      setLen(i);
    }, speed);
    return () => clearInterval(timer);
  }, [text, enabled, speed]);
  return { visible: text.slice(0, len), done: doneRef.current };
}

function formatTokens(n?: number): string {
  if (!n || n <= 0) return '';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

/** 从内容首行生成安全文件名 */
function makeDocFilename(content: string, ext: string): string {
  const first = (content.split(/\n/)[0] || 'AI对话内容').trim().slice(0, 24);
  const safe = first.replace(/[\\/:*?"<>|\r\n]/g, '_') || 'AI对话内容';
  return `${safe}.${ext}`;
}

/** 简易 Markdown → HTML（导出 .html 文档用，浏览器/Word 可打开） */
function contentToHtml(content: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  let html = content.replace(/(```[\w]*\n?[\s\S]*?```)/g, (m) => {
    const inner = m.replace(/^```[\w]*\n?/, '').replace(/```$/, '');
    return `<pre style="background:#0f172a;color:#e2e8f0;padding:12px;border-radius:8px;overflow-x:auto">${esc(inner.trim())}</pre>`;
  });
  html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');
  html = html.replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/`([^`]+)`/g, '<code style="background:#eef2ff;color:#4338ca;padding:1px 5px;border-radius:4px">$1</code>');
  html = html.split(/\n{2,}/).map((p) => {
    const t = p.trim();
    if (!t) return '';
    if (/^<(h1|h2|h3|ul|pre)/.test(t)) return t;
    return `<p>${t.replace(/\n/g, '<br/>')}</p>`;
  }).join('\n');
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width, initial-scale=1.0"/>
<title>${esc(content.slice(0, 40))}</title>
<style>body{font-family:-apple-system,'PingFang SC','Microsoft YaHei',sans-serif;max-width:860px;margin:32px auto;padding:0 20px;color:#1f2937;line-height:1.75}h1,h2,h3{color:#111827}ul{padding-left:1.4em}blockquote{border-left:3px solid #6366f1;margin:0;padding-left:14px;color:#4b5563}</style>
</head>
<body>${html}</body>
</html>`;
}

/** 客户端直接下载 Blob 文档 */
function downloadAsFile(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// 简单的 Markdown 渲染（颜色化代码块，Codex 风格深色代码区）
function renderContent(content: string): React.ReactNode {
  const segments = content.split(/(```[\s\S]*?```)/g);
  return segments.map((seg, i) => {
    if (seg.startsWith('```')) {
      const langEnd = seg.indexOf('\n');
      const lang = langEnd > 0 ? seg.slice(3, langEnd).trim() : '';
      const code = langEnd > 0 ? seg.slice(langEnd + 1, -3).trim() : seg.slice(3, -3).trim();
      return (
        <div key={i} className="code-block">
          {lang && <div className="code-lang">{lang}</div>}
          <pre><code>{code}</code></pre>
        </div>
      );
    }
    // 简单 Markdown 处理
    return (
      <span key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {seg.split(/(\*\*.*?\*\*|`.*?`|\n)/g).map((token, j) => {
          if (token.startsWith('**') && token.endsWith('**')) {
            return <strong key={j}>{token.slice(2, -2)}</strong>;
          }
          if (token.startsWith('`') && token.endsWith('`')) {
            return <code key={j} className="inline-code">{token.slice(1, -1)}</code>;
          }
          if (token === '\n') return <br key={j} />;
          return <span key={j}>{token}</span>;
        })}
      </span>
    );
  });
}

export default function ChatMessageBubble({ msg, onStreamDone }: Props) {
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const isUser = msg.role === 'user';
  const streaming = Boolean(msg.streaming);

  // 流式打字展示（仅 streaming 标记存在时启用）
  const { visible, done } = useTypewriter(msg.content, streaming);
  useEffect(() => {
    if (done && streaming) onStreamDone?.(msg.id);
  }, [done, streaming, msg.id, onStreamDone]);

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content).then(() => {
      message.success('已复制');
    }).catch(() => {
      message.error('复制失败');
    });
  };

  const handleDownload = async ({ key }: { key: string }) => {
    if (!msg.content) return;
    if (key === 'link') {
      // 服务端生成可分享/下载链接
      try {
        const res: any = await aiAPI.exportDoc({ title: makeDocFilename(msg.content, 'md'), content: msg.content, format: 'md' });
        const url = res?.downloadUrl || res?.url;
        if (url) {
          await navigator.clipboard.writeText(url);
          message.success(`下载链接已生成并复制：${url}`);
        } else {
          message.error('链接生成失败');
        }
      } catch (err: any) {
        message.error(err?.response?.data?.error || '链接生成失败');
      }
      return;
    }
    const fmt = key === 'html' || key === 'txt' ? key : 'md';
    const filename = makeDocFilename(msg.content, fmt);
    if (fmt === 'html') downloadAsFile(contentToHtml(msg.content), filename, 'text/html;charset=utf-8');
    else if (fmt === 'txt') {
      const text = msg.content
        .replace(/(```[\w]*\n?[\s\S]*?```)/g, (_m, code) => code.replace(/^```[\w]*\n?/, '').replace(/```$/, ''))
        .replace(/^#{1,6}\s+/gm, '').replace(/\*\*/g, '');
      downloadAsFile(text, filename, 'text/plain;charset=utf-8');
    } else downloadAsFile(msg.content, filename, 'text/markdown;charset=utf-8');
    message.success(`已下载：${filename}`);
  };

  const downloadItems = [
    { key: 'md', icon: <FileTextOutlined />, label: '下载 Markdown (.md)' },
    { key: 'html', icon: <FileTextOutlined />, label: '下载 HTML 文档 (.html)' },
    { key: 'txt', icon: <FileTextOutlined />, label: '下载纯文本 (.txt)' },
    { type: 'divider' as const },
    { key: 'link', icon: <LinkOutlined />, label: '生成下载链接（可分享）' },
  ];

  const tokens = msg.usage?.totalTokens || msg.usage?.promptTokens || 0;
  const showMeta = !isUser && (Boolean(msg.model) || tokens > 0 || (msg.creditsCharged || 0) > 0);

  return (
    <div className={`chat-bubble ${isUser ? 'user' : 'assistant'}`}>
      {/* 头像 */}
      <div className={`bubble-avatar ${isUser ? 'user' : 'ai'}`}>
        {isUser ? <UserOutlined /> : <RobotOutlined />}
      </div>

      {/* 内容 */}
      <div className="bubble-content">
        {/* 发送者 + 模型 */}
        <div className="bubble-header">
          <Text strong className="bubble-author">
            {isUser ? '你' : 'AI 助手'}
          </Text>
          {isUser && msg.optimized && <span className="optimized-chip">✨ 已优化</span>}
          {!isUser && msg.model && (
            <span className="model-chip">{cleanModelDisplay(msg.model)}</span>
          )}
          {!isUser && (
            <Space size={0} className="bubble-actions">
              <Button type="text" size="small" icon={<CopyOutlined />} onClick={handleCopy} className="copy-btn" title="复制原格式" />
              <Dropdown menu={{ items: downloadItems, onClick: handleDownload }} trigger={['click']} placement="bottomRight">
                <Button type="text" size="small" icon={<DownloadOutlined />} className="copy-btn" title="下载文档 / 生成下载链接" />
              </Dropdown>
            </Space>
          )}
        </div>

        {/* 思考过程（仅 AI 消息） */}
        {!isUser && msg.thinking && (
          <div className="thinking-area">
            <div className="thinking-toggle" onClick={() => setThinkingOpen(!thinkingOpen)}>
              {thinkingOpen ? <DownOutlined /> : <RightOutlined />}
              <span>思考过程</span>
            </div>
            {thinkingOpen && (
              <div className="thinking-content">{msg.thinking}</div>
            )}
          </div>
        )}

        {/* 消息内容（streaming 时逐字展示 + 光标） */}
        <div className="bubble-text">
          {isUser
            ? msg.content
            : renderContent(streaming ? visible : msg.content)}
          {streaming && !done && <span className="stream-cursor" />}
        </div>

        {/* 每次对话用量统计：模型 · tokens · 积分 */}
        {showMeta && (
          <div className="bubble-meta">
            {msg.model && <span className="meta-item"><ThunderboltOutlined /> {cleanModelDisplay(msg.model)}</span>}
            {tokens > 0 && <span className="meta-item">tokens {formatTokens(tokens)}</span>}
            {(msg.creditsCharged || 0) > 0 && <span className="meta-item"><GiftOutlined /> -{msg.creditsCharged} 积分</span>}
          </div>
        )}
      </div>

      <style>{`
        .chat-bubble {
          display: flex; gap: 10px; padding: 6px 0; align-items: flex-start;
          animation: bubbleIn 0.22s ease;
        }
        @keyframes bubbleIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .chat-bubble.user { flex-direction: row; }

        .bubble-avatar {
          width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 14px;
        }
        .bubble-avatar.ai {
          background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
        }
        .bubble-avatar.user { background: rgba(148, 163, 184, 0.18); color: var(--text-secondary); }

        .bubble-content { max-width: 78%; min-width: 120px; }
        .bubble-header { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
        .bubble-author { font-size: 12px; color: var(--text-secondary); }
        .model-chip {
          font-size: 11px; color: var(--text-tertiary);
          background: rgba(148, 163, 184, 0.12);
          border: 1px solid var(--border); border-radius: 6px; padding: 0 7px; line-height: 18px;
          font-family: 'JetBrains Mono', 'Fira Code', monospace;
        }
        .optimized-chip {
          font-size: 10.5px; color: #8b5cf6; background: rgba(139, 92, 246, 0.12);
          border: 1px solid rgba(139, 92, 246, 0.3); border-radius: 10px; padding: 0 7px; line-height: 16px;
        }
        .bubble-actions { opacity: 0; transition: opacity 0.2s; }
        .chat-bubble:hover .bubble-actions { opacity: 1; }
        .bubble-actions .copy-btn { opacity: 1; }
        .copy-btn { color: var(--text-tertiary); }
        .chat-bubble:hover .copy-btn { opacity: 1; }
        .chat-bubble:hover .copy-btn { opacity: 1; }

        .bubble-text {
          padding: 10px 14px; border-radius: 12px; font-size: 14px; line-height: 1.7;
          word-break: break-word; border: 1px solid var(--border);
        }
        .chat-bubble.user .bubble-text {
          background: rgba(99, 102, 241, 0.08);
          border-color: rgba(99, 102, 241, 0.22); color: var(--text-primary);
        }
        .chat-bubble.assistant .bubble-text { background: var(--bg-container); }

        .inline-code {
          background: rgba(148, 163, 184, 0.14); padding: 1px 6px; border-radius: 5px;
          font-size: 12.5px; font-family: 'JetBrains Mono', 'Fira Code', monospace;
          color: var(--text-primary);
        }
        .code-block {
          background: #16161e; color: #cdd6f4; border-radius: 10px;
          margin: 8px 0; overflow: hidden; border: 1px solid rgba(255,255,255,0.06);
        }
        .code-lang {
          padding: 5px 12px; font-size: 11px; color: #8b8fa3;
          background: rgba(255,255,255,0.04); border-bottom: 1px solid rgba(255,255,255,0.06);
          font-family: 'JetBrains Mono', 'Fira Code', monospace; text-transform: uppercase; letter-spacing: 0.5px;
        }
        .code-block pre { margin: 0; padding: 12px; overflow-x: auto; font-size: 13px; font-family: 'JetBrains Mono', 'Fira Code', monospace; }

        .stream-cursor {
          display: inline-block; width: 8px; height: 15px; margin-left: 2px;
          background: #6366f1; vertical-align: -2px; border-radius: 2px;
          animation: cursorBlink 0.9s steps(2, start) infinite;
        }
        @keyframes cursorBlink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }

        .thinking-area { margin: 4px 0 8px; background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.25); border-radius: 8px; overflow: hidden; }
        .thinking-toggle { padding: 6px 10px; font-size: 12px; color: #d97706; cursor: pointer; display: flex; align-items: center; gap: 6px; }
        .thinking-content { padding: 6px 10px; font-size: 12px; color: var(--text-secondary); border-top: 1px solid rgba(245, 158, 11, 0.15); white-space: pre-wrap; word-break: break-word; }

        .bubble-meta {
          display: flex; gap: 10px; margin-top: 4px; padding-left: 2px;
          font-size: 11px; color: var(--text-tertiary); align-items: center;
        }
        .meta-item { display: inline-flex; align-items: center; gap: 4px; }
        .meta-item svg { font-size: 10px; }

        @media (max-width: 768px) { .bubble-content { max-width: 88%; } }
      `}</style>
    </div>
  );
}
